import React from 'react';
import { DropdownSelectOptionType, Matcher } from './DropdownSelect';
import { DropdownSelectOption } from './DropdownSelectOption';
import { DropdownSelectLabel } from './DropdownSelectLabel';

type ProcessOptionsReturnType = {
    heightMap: Map<number, number>;
    displayLabel: string | React.ReactNode | undefined;
    children: JSX.Element[];
};

function compileItem<T>(
    acc: ProcessOptionsReturnType,
    currentIndex: number,
    currentOptions: DropdownSelectOptionType<T>,
    selectedValue?: T,
) {
    acc.displayLabel = selectedValue === currentOptions.value ? currentOptions.label : acc.displayLabel;
    const props = { ...currentOptions, key: currentIndex, selectedValue };
    if (currentOptions.Component) {
        acc.heightMap.set(currentIndex, currentOptions.height || 40);
        acc.children.push(<currentOptions.Component {...props} />);
    } else {
        acc.heightMap.set(currentIndex, 40);
        acc.children.push(<DropdownSelectOption<T> {...props}>{props.label}</DropdownSelectOption>);
    }
}

export function processOptions<T>(
    options: DropdownSelectOptionType<T>[],
    onChange: (value: T) => void,
    selectedValue?: T,
) {
    return options.reduce(
        (acc, option) => {
            const currentCount = acc.children.length;
            if (option.options) {
                acc.heightMap.set(currentCount, 40);
                acc.children.push(<DropdownSelectLabel key={currentCount} label={option.label || ''} />);
                option.options.forEach((childOption, k) => {
                    compileItem(acc, currentCount + k + 1, { ...childOption, onChange }, selectedValue);
                });
                return acc;
            }
            compileItem(acc, currentCount, { ...option, onChange }, selectedValue);
            return acc;
        },
        {
            heightMap: new Map(),
            displayLabel: undefined,
            children: [],
        } as ProcessOptionsReturnType,
    );
}

export function onFilterChange<T>(term: string, filterFunction: Matcher<T>, options: DropdownSelectOptionType<T>[]) {
    return options.reduce((acc, item) => {
        if (item.options) {
            const newOptions = item.options.filter(option => filterFunction(term, option));
            if (newOptions.length) {
                acc.push({ ...item, options: newOptions });
            }
        } else {
            if (filterFunction(term, item)) {
                acc.push(item);
            }
        }
        return acc;
    }, [] as DropdownSelectOptionType<T>[]);
}
